import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Search, Plus, Trash2, Settings2, Copy, X, Check, AlertTriangle,
} from 'lucide-react';
import api from '../services/api';
import '../styles/GestioneOrari.css';

/**
 * GestioneOrari — chi lavora quando.
 *
 * Sta fuori dall'agenda perche' e' configurazione, non uso quotidiano:
 * ci mettono mano admin e segreteria quando un medico cambia giorni.
 *
 * I sette giorni sono sempre visibili con un interruttore. Un giorno
 * acceso ha almeno una fascia; spegnendolo si chiudono tutte. Le
 * fasce gia' entrate in vigore non spariscono: si chiudono a ieri,
 * cosi' resta la storia di com'era l'orario mentre valeva.
 */

const GIORNI = [
    { n: 1, nome: 'Lunedì' },
    { n: 2, nome: 'Martedì' },
    { n: 3, nome: 'Mercoledì' },
    { n: 4, nome: 'Giovedì' },
    { n: 5, nome: 'Venerdì' },
    { n: 6, nome: 'Sabato' },
    { n: 7, nome: 'Domenica' },
];

/* La griglia dell'agenda e' al quarto d'ora: le tendine seguono quella. */
const ORARI = [];
for (let h = 6; h <= 22; h += 1) {
    for (let m = 0; m < 60; m += 15) {
        if (h === 22 && m > 0) break;
        ORARI.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
}

/** "08:45:00" dal database diventa "08:45" per le tendine. */
const hhmm = (t) => (t ? String(t).slice(0, 5) : '');

const etichettaRicorrenza = (f) => {
    if (f.ricorrenza === 'quindicinale') {
        return f.ricorrenza_rif === 0 ? 'settimane alterne (A)' : 'settimane alterne (B)';
    }
    if (f.ricorrenza === 'mensile') {
        if (f.ricorrenza_rif === -1) return 'ultima del mese';
        return `${f.ricorrenza_rif}ª del mese`;
    }
    return null;
};

const GestioneOrari = () => {
    const [agende, setAgende] = useState([]);
    const [stanze, setStanze] = useState([]);
    const [sel, setSel] = useState(null);
    const [fasce, setFasce] = useState([]);

    const [cerca, setCerca] = useState('');
    const [spec, setSpec] = useState('');

    const [caricamento, setCaricamento] = useState(true);
    const [errore, setErrore] = useState(null);
    const [avvisi, setAvvisi] = useState([]);
    const [salvato, setSalvato] = useState(false);

    const [opzioni, setOpzioni] = useState(null);   // fascia in dettaglio
    const [copiaDa, setCopiaDa] = useState(false);

    /* ---------- caricamento ---------- */

    const caricaAgende = useCallback(async () => {
        try {
            const r = await api.get('/agenda/orari/agende');
            setAgende(r.data.data || []);
        } catch (e) {
            setErrore('Non riesco a caricare le agende');
        } finally {
            setCaricamento(false);
        }
    }, []);

    const caricaFasce = useCallback(async (agendaId) => {
        if (!agendaId) return;
        try {
            const r = await api.get('/agenda/orari', { params: { agenda_id: agendaId } });
            setFasce(r.data.data || []);
        } catch (e) {
            setErrore('Non riesco a caricare gli orari');
        }
    }, []);

    useEffect(() => {
        caricaAgende();
        api.get('/agenda/stanze')
            .then((r) => setStanze(r.data.data?.stanze || r.data.data || []))
            .catch(() => setStanze([]));
    }, [caricaAgende]);

    useEffect(() => { caricaFasce(sel); }, [sel, caricaFasce]);

    /* ---------- filtri dell'elenco ---------- */

    /* L'elenco, non la stringa. Fino al 20 settembre 2026 il filtro
       confrontava la colonna `specializzazione` INTERA: chi ne aveva due
       scritte con una barra - «Fisiatria/Medicina Estetica» - non
       compariva sotto nessuna delle due, ma creava un gruppo tutto suo
       con dentro una persona sola. E chi ne aveva due e ne aveva scritta
       una sola non compariva affatto sotto l'altra.
       Il ripiego sulla stringa serve alle agende della struttura, che un
       medico non ce l'hanno, e a un frontend piu' nuovo del server. */
    const specDi = (a) => (a.specializzazioni?.length
        ? a.specializzazioni
        : (a.specializzazione ? [a.specializzazione] : []));

    const specializzazioni = useMemo(() => {
        const s = new Set();
        agende.forEach((a) => specDi(a).forEach((x) => s.add(x)));
        return [...s].sort((x, y) => x.localeCompare(y, 'it'));
    }, [agende]);

    const elenco = useMemo(() => {
        const q = cerca.trim().toLowerCase();
        return agende.filter((a) => {
            if (spec && !specDi(a).includes(spec)) return false;
            if (!q) return true;
            const nome = `${a.cognome || ''} ${a.medico_nome || ''} ${a.nome} `
                       + specDi(a).join(' ');
            return nome.toLowerCase().includes(q);
        });
    }, [agende, cerca, spec]);

    const agendaSel = agende.find((a) => a.id === sel);

    /* ---------- scrittura ---------- */

    const dopoScrittura = async (risposta) => {
        setAvvisi(risposta?.data?.avvisi || []);
        setSalvato(true);
        setTimeout(() => setSalvato(false), 1500);
        await caricaFasce(sel);
        caricaAgende();
    };

    const aggiungiFascia = async (giorno, base) => {
        setErrore(null);
        try {
            const r = await api.post('/agenda/orari', {
                agenda_id: sel,
                giorno,
                ora_inizio: base?.ora_inizio || '09:00',
                ora_fine: base?.ora_fine || '13:00',
                stanza_id: base?.stanza_id || null,
            });
            await dopoScrittura(r);
        } catch (e) {
            setErrore(e.response?.data?.message || 'Errore nel salvataggio');
        }
    };

    const modificaFascia = async (id, campi) => {
        setErrore(null);
        try {
            const r = await api.put(`/agenda/orari/${id}`, campi);
            await dopoScrittura(r);
        } catch (e) {
            setErrore(e.response?.data?.message || 'Errore nella modifica');
        }
    };

    const eliminaFascia = async (id) => {
        setErrore(null);
        try {
            await api.delete(`/agenda/orari/${id}`);
            await dopoScrittura(null);
        } catch (e) {
            setErrore(e.response?.data?.message || 'Errore nella cancellazione');
        }
    };

    /** L'interruttore del giorno: accende creando una fascia, spegne chiudendole tutte. */
    const commutaGiorno = async (giorno, attivo) => {
        if (attivo) {
            await aggiungiFascia(giorno);
        } else {
            const daChiudere = fasce.filter((f) => f.giorno === giorno && !f.archiviata);
            for (const f of daChiudere) {
                // eslint-disable-next-line no-await-in-loop
                await api.delete(`/agenda/orari/${f.id}`).catch(() => null);
            }
            await dopoScrittura(null);
        }
    };

    /* La durata di default vale quando si prenota senza scegliere
       una prestazione: e' lo slot naturale di quel medico. */
    const cambiaDurata = async (v) => {
        const d = parseInt(v, 10);
        if (!(d >= 5 && d <= 240)) return;
        setAgende((el) => el.map((a) => (
            a.id === sel ? { ...a, durata_default_minuti: d } : a
        )));
        try {
            await api.put(`/agenda/orari/agenda/${sel}`, { durata_default_minuti: d });
            setSalvato(true);
            setTimeout(() => setSalvato(false), 1500);
        } catch (e) {
            setErrore(e.response?.data?.message || 'Errore nel salvataggio della durata');
            caricaAgende();
        }
    };

    const copiaOrari = async (daId) => {
        setErrore(null);
        try {
            const r = await api.post('/agenda/orari/copia', {
                da_agenda_id: daId, a_agenda_id: sel,
            });
            setCopiaDa(false);
            setAvvisi([`Copiate ${r.data.copiate} fasce`]);
            await caricaFasce(sel);
            caricaAgende();
        } catch (e) {
            setErrore(e.response?.data?.message || 'Errore nella copia');
        }
    };

    /* ---------- resa ---------- */

    const perGiorno = (n) => fasce.filter((f) => f.giorno === n && !f.archiviata);

    return (
        <div className="go-wrap">
            <aside className="go-elenco">
                <h2>Agende</h2>

                <div className="go-cerca" data-non-trattiene>
                    <Search size={15} />
                    <input
                        value={cerca}
                        onChange={(e) => setCerca(e.target.value)}
                        placeholder="Cerca per nome"
                    />
                </div>

                <select value={spec} onChange={(e) => setSpec(e.target.value)} className="go-spec">
                    <option value="">Tutte le specializzazioni</option>
                    {specializzazioni.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>

                <div className="go-lista">
                    {caricamento && <p className="go-vuoto">Caricamento…</p>}
                    {!caricamento && !elenco.length && <p className="go-vuoto">Nessuna agenda</p>}
                    {elenco.map((a) => (
                        <button
                            key={a.id}
                            className={`go-voce ${sel === a.id ? 'attiva' : ''}`}
                            onClick={() => setSel(a.id)}
                        >
                            <span className="go-voce-nome">
                                {a.cognome ? `${a.cognome}, ${a.medico_nome}` : a.nome}
                                {/* Tutte, separate da una virgola: la
                                    riga dell'elenco e' il posto dove si
                                    verifica che il filtro abbia pescato
                                    la persona giusta. */}
                                {specDi(a).length > 0 && <em>{specDi(a).join(', ')}</em>}
                            </span>
                            <span className={`go-conteggio ${a.fasce_attive ? '' : 'zero'}`}>
                                {a.fasce_attive || '—'}
                            </span>
                        </button>
                    ))}
                </div>
            </aside>

            <main className="go-dettaglio">
                {!sel && (
                    <div className="go-nessuna">
                        <p>Scegli un&apos;agenda per impostarne gli orari.</p>
                        <p className="go-nota">
                            Il numero a destra di ogni nome è quante fasce di lavoro ha oggi.
                        </p>
                    </div>
                )}

                {sel && (
                    <>
                        <header className="go-testata">
                            <div>
                                <h1>
                                    {agendaSel?.cognome
                                        ? `${agendaSel.cognome}, ${agendaSel.medico_nome}`
                                        : agendaSel?.nome}
                                </h1>
                                {agendaSel?.specializzazione && <p>{agendaSel.specializzazione}</p>}
                            </div>
                            <div className="go-azioni">
                                <label className="go-durata">
                                    Slot
                                    <input
                                        type="number"
                                        min="5"
                                        max="240"
                                        step="5"
                                        value={agendaSel?.durata_default_minuti ?? 30}
                                        onChange={(e) => cambiaDurata(e.target.value)}
                                    />
                                    min
                                </label>
                                {salvato && <span className="go-salvato"><Check size={15} /> salvato</span>}
                                <button className="go-btn" onClick={() => setCopiaDa(true)}>
                                    <Copy size={15} /> Copia da…
                                </button>
                            </div>
                        </header>

                        {errore && <div className="go-errore">{errore}</div>}
                        {avvisi.map((a, i) => (
                            <div className="go-avviso" key={i}><AlertTriangle size={15} /> {a}</div>
                        ))}

                        <div className="go-giorni">
                            {GIORNI.map((g) => {
                                const righe = perGiorno(g.n);
                                const acceso = righe.length > 0;
                                return (
                                    <section key={g.n} className={`go-giorno ${acceso ? 'acceso' : ''}`}>
                                        <div className="go-giorno-testa">
                                            <button
                                                className={`go-switch ${acceso ? 'on' : ''}`}
                                                onClick={() => commutaGiorno(g.n, !acceso)}
                                                aria-label={g.nome}
                                            >
                                                <span />
                                            </button>
                                            <b>{g.nome}</b>
                                        </div>

                                        {acceso && (
                                            <div className="go-fasce">
                                                {righe.map((f) => (
                                                    <div key={f.id} className="go-fascia">
                                                        <select
                                                            value={hhmm(f.ora_inizio)}
                                                            onChange={(e) => modificaFascia(f.id, { ora_inizio: e.target.value })}
                                                        >
                                                            {ORARI.map((o) => <option key={o} value={o}>{o}</option>)}
                                                        </select>
                                                        <span className="go-sep">:</span>
                                                        <select
                                                            value={hhmm(f.ora_fine)}
                                                            onChange={(e) => modificaFascia(f.id, { ora_fine: e.target.value })}
                                                        >
                                                            {ORARI.map((o) => <option key={o} value={o}>{o}</option>)}
                                                        </select>

                                                        <select
                                                            className="go-stanza"
                                                            value={f.stanza_id || ''}
                                                            onChange={(e) => modificaFascia(f.id, { stanza_id: e.target.value || null })}
                                                        >
                                                            <option value="">nessuna stanza</option>
                                                            {stanze.map((s) => (
                                                                <option key={s.id} value={s.id}>{s.nome}</option>
                                                            ))}
                                                        </select>

                                                        {etichettaRicorrenza(f) && (
                                                            <span className="go-tag">{etichettaRicorrenza(f)}</span>
                                                        )}
                                                        {!f.prenotabile_online && (
                                                            <span className="go-tag spento">non online</span>
                                                        )}

                                                        <button
                                                            className="go-icona"
                                                            title="Ricorrenza, validità, prenotazione online"
                                                            onClick={() => setOpzioni(f)}
                                                        >
                                                            <Settings2 size={16} />
                                                        </button>
                                                        <button
                                                            className="go-icona pericolo"
                                                            title="Togli questa fascia"
                                                            onClick={() => eliminaFascia(f.id)}
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                ))}

                                                <button
                                                    className="go-aggiungi"
                                                    onClick={() => aggiungiFascia(g.n, righe[righe.length - 1])}
                                                >
                                                    <Plus size={14} /> Aggiungi un altro orario
                                                </button>
                                            </div>
                                        )}
                                    </section>
                                );
                            })}
                        </div>
                    </>
                )}
            </main>

            {opzioni && (
                <PannelloOpzioni
                    fascia={opzioni}
                    onChiudi={() => setOpzioni(null)}
                    onSalva={async (campi) => { await modificaFascia(opzioni.id, campi); setOpzioni(null); }}
                />
            )}

            {copiaDa && (
                <div className="go-modale-fondo" onClick={() => setCopiaDa(false)}>
                    <div className="go-modale" onClick={(e) => e.stopPropagation()}>
                        <header>
                            <h3>Copia gli orari da un&apos;altra agenda</h3>
                            <button onClick={() => setCopiaDa(false)}><X size={18} /></button>
                        </header>
                        <p className="go-nota">
                            Le fasce in vigore vengono aggiunte a questa agenda. Quelle già presenti restano.
                        </p>
                        <div className="go-lista-copia">
                            {agende.filter((a) => a.id !== sel && a.fasce_attive > 0).map((a) => (
                                <button key={a.id} onClick={() => copiaOrari(a.id)}>
                                    {a.cognome ? `${a.cognome}, ${a.medico_nome}` : a.nome}
                                    <em>{a.fasce_attive} fasce</em>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/* ---------- dettaglio di una fascia ---------- */

const PannelloOpzioni = ({ fascia, onChiudi, onSalva }) => {
    const [ric, setRic] = useState(fascia.ricorrenza || 'settimanale');
    const [rif, setRif] = useState(
        fascia.ricorrenza_rif === null || fascia.ricorrenza_rif === undefined
            ? '' : String(fascia.ricorrenza_rif)
    );
    const [online, setOnline] = useState(fascia.prenotabile_online !== false);
    const [dal, setDal] = useState(fascia.valido_dal ? String(fascia.valido_dal).slice(0, 10) : '');
    const [al, setAl] = useState(fascia.valido_al ? String(fascia.valido_al).slice(0, 10) : '');

    const cambiaRic = (v) => {
        setRic(v);
        if (v === 'settimanale') setRif('');
        else if (v === 'quindicinale') setRif('0');
        else setRif('1');
    };

    return (
        <div className="go-modale-fondo" onClick={onChiudi}>
            <div className="go-modale" onClick={(e) => e.stopPropagation()}>
                <header>
                    <h3>Opzioni per {hhmm(fascia.ora_inizio)} – {hhmm(fascia.ora_fine)}</h3>
                    <button onClick={onChiudi}><X size={18} /></button>
                </header>

                <label className="go-campo">
                    <span>Ogni quanto si ripete</span>
                    <select value={ric} onChange={(e) => cambiaRic(e.target.value)}>
                        <option value="settimanale">Ogni settimana</option>
                        <option value="quindicinale">A settimane alterne</option>
                        <option value="mensile">Una volta al mese</option>
                    </select>
                </label>

                {ric === 'quindicinale' && (
                    <label className="go-campo">
                        <span>Quale delle due settimane</span>
                        <select value={rif} onChange={(e) => setRif(e.target.value)}>
                            <option value="0">Settimana A (quella di inizio validità)</option>
                            <option value="1">Settimana B (quella dopo)</option>
                        </select>
                    </label>
                )}

                {ric === 'mensile' && (
                    <label className="go-campo">
                        <span>Quale settimana del mese</span>
                        <select value={rif} onChange={(e) => setRif(e.target.value)}>
                            <option value="1">La prima</option>
                            <option value="2">La seconda</option>
                            <option value="3">La terza</option>
                            <option value="4">La quarta</option>
                            <option value="5">La quinta</option>
                            <option value="-1">L&apos;ultima</option>
                        </select>
                    </label>
                )}

                <label className="go-campo-riga">
                    <input type="checkbox" checked={online} onChange={(e) => setOnline(e.target.checked)} />
                    <span>Prenotabile online</span>
                </label>

                <div className="go-campi-doppi">
                    <label className="go-campo">
                        <span>Vale dal</span>
                        <input type="date" value={dal} onChange={(e) => setDal(e.target.value)} />
                    </label>
                    <label className="go-campo">
                        <span>Fino al (vuoto = sempre)</span>
                        <input type="date" value={al} onChange={(e) => setAl(e.target.value)} />
                    </label>
                </div>

                <footer>
                    <button className="go-btn" onClick={onChiudi}>Annulla</button>
                    <button
                        className="go-btn primario"
                        onClick={() => onSalva({
                            ricorrenza: ric,
                            ricorrenza_rif: ric === 'settimanale' ? null : parseInt(rif, 10),
                            prenotabile_online: online,
                            valido_dal: dal || undefined,
                            valido_al: al || null,
                        })}
                    >
                        Salva
                    </button>
                </footer>
            </div>
        </div>
    );
};

export default GestioneOrari;
